import { parse } from '@tinyhttp/content-disposition';
import { Observable, Subject } from 'rxjs';

export interface FileContents {
  filename: string;
  dataURL: string;
  contentsBase64: string;
  mimeType: string;
}

export class FileHelper {

  public static saveAsFile(data: object | Blob, filename: string) {
    const a = document.createElement('a');
    const file = FileHelper.getData(data);
    a.href = URL.createObjectURL(file);
    a.style.display = 'none';
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  public static extractFileNameFromContentDispositionHeader(contentDispositionHeader: string, defaultName = 'file') {
    if(contentDispositionHeader === null) {
      return defaultName;
    }
    try {
      return parse(contentDispositionHeader).parameters['filename'] as string || defaultName;
    } catch (_ignored) {
      return defaultName;
    }
  }

  private static getData(data: object | Blob) {
    if (data instanceof Blob) {
      return data;
    }
    try {
      const jsonData = JSON.stringify(data);
      new Blob([jsonData], { type: 'application/json' });
    } catch (e) {
      // ignore error
    }
    return new Blob([], { type: 'application/json' });
  }

  public static byteCountToDisplaySize(sizeBytes: number | bigint | null | undefined): string {
    // https://stackoverflow.com/a/72596863
    if (sizeBytes === null || typeof sizeBytes === 'undefined') {
      return '';
    }
    const UNITS = [ 'byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte', 'petabyte' ];
    const BYTES_PER_KB = 1000;
    let size = Math.abs(Number(sizeBytes));
    let u = 0;
    while(size >= BYTES_PER_KB && u < UNITS.length-1) {
      size /= BYTES_PER_KB;
      ++u;
    }
    return new Intl.NumberFormat([], { style: 'unit', unit: UNITS[u], unitDisplay: 'short', maximumFractionDigits: 1 })
      .format(size);
  }

  public static readFileContents(file: File | Blob): Observable<FileContents | null> {
    const subject = new Subject<FileContents | null>();
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (!e.target) {
        subject.next(null);
        subject.complete();
        return;
      }
      if (typeof e.target.result !== 'string') {
        subject.error(new Error('Expected string because we called readAsDataURL()'));
        return;
      }
      const result: string = e.target.result;
      const parsed = result != null ? FileHelper.parseDataUrl(result) : null;
      if (parsed != null) {
        subject.next({ filename: file instanceof File ? file.name : 'blob', dataURL: result, ...parsed });
      } else {
        subject.next(null);
      }
      subject.complete();
    };
    reader.readAsDataURL(file);
    return subject.asObservable();
  }

  public static parseDataUrl(dataUrl: string): { contentsBase64: string; mimeType: string } | null {
    const base64Idx = dataUrl.indexOf(';base64,');
    if (dataUrl.startsWith('data:') && base64Idx !== -1) {
      const mimeType = dataUrl.substring(5, base64Idx);
      const contentsBase64 = dataUrl.substring(base64Idx + 8);
      return { contentsBase64, mimeType };
    } else {
      return null;
    }
  }
}
